import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../contexts/AuthContext";
import { useBroadcast } from "./useBroadcast";
import { userTopic } from "../lib/realtime/topics";
import { parseChange } from "../lib/realtime/payloads";

export interface ChunkInfo {
  id: string;
  chunkIndex: number;
  totalChunks: number;
  status: "pending" | "processing" | "completed" | "failed";
  ordersCount: number;
  storagePath?: string;
  errorMessage?: string;
  startedAt?: string;
  completedAt?: string;
}

const CHUNK_EVENTS = ["job_chunks_change"] as const;
const NO_TOPICS: readonly string[] = [];

/** Effet d'un message sur une tranche : posée/remplacée (valeur), ou retirée
 * (null) — ligne d'une autre tâche, ou ping sans id connu : aucun effet. */
type ChunkEffect = { id: string; value: ChunkInfo | null } | null;

function computeChunkEffect(payload: Record<string, unknown>, jobId: string): ChunkEffect {
  const change = parseChange(payload);
  if (change.kind === "row") {
    const id = String(change.record.id);
    if (change.record.parent_job_id !== jobId) return { id, value: null };
    return { id, value: mapChunk(change.record) };
  }
  const { id } = change;
  return id ? { id, value: null } : null;
}

function applyEffect(prev: ChunkInfo[], effect: ChunkEffect): ChunkInfo[] {
  if (!effect) return prev;
  if (effect.value === null) {
    return prev.some((c) => c.id === effect.id) ? prev.filter((c) => c.id !== effect.id) : prev;
  }
  return [...prev.filter((c) => c.id !== effect.value!.id), effect.value].sort(
    (a, b) => a.chunkIndex - b.chunkIndex
  );
}

/**
 * Applique un message `job_chunks_change` de user:<uid> aux tranches de
 * `jobId`. Ligne de cette tâche : ajoutée ou remplacée, ordre des index gardé ;
 * ligne d'une autre tâche (ou réassignée à une autre tâche) : retirée si elle
 * était affichée ; ping (DELETE) : retirée par son `id`.
 */
export function applyChunkChange(
  prev: ChunkInfo[],
  payload: Record<string, unknown>,
  jobId: string
): ChunkInfo[] {
  return applyEffect(prev, computeChunkEffect(payload, jobId));
}

/**
 * Instantané relu après un chargement ou un rattrapage, fusionné avec les
 * messages directs reçus pendant que ce chargement était en vol : une tranche
 * posée/retirée en direct depuis le début du chargement garde son état
 * (direct) même si l'instantané revient avec une version plus ancienne —
 * mirroring mergeJobsSnapshot (useJobCenter.tsx).
 */
export function mergeChunksSnapshot(
  serverRows: ChunkInfo[],
  liveSinceStart: ReadonlyMap<string, ChunkInfo | null>
): ChunkInfo[] {
  const byId = new Map(serverRows.map((c) => [c.id, c] as const));
  for (const [id, value] of liveSinceStart) {
    if (value === null) byId.delete(id);
    else byId.set(id, value);
  }
  return [...byId.values()].sort((a, b) => a.chunkIndex - b.chunkIndex);
}

export function useChunkProgress(jobId: string | null) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [chunks, setChunks] = useState<ChunkInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const jobIdRef = useRef(jobId);
  jobIdRef.current = jobId;
  // Un chargement (initial ou rattrapage) peut être lancé pendant qu'un
  // précédent est encore en vol (ex. resync pendant le chargement initial).
  // Chaque appel a sa propre carte (jamais partagée, jamais réinitialisée par
  // un autre appel) et un numéro de séquence : un message direct reçu
  // pendant la fenêtre est rejoué sur TOUTES les cartes en vol ; au retour,
  // un instantané plus ancien qu'un déjà appliqué ne doit jamais l'écraser.
  const seqRef = useRef(0);
  const lastAppliedSeqRef = useRef(0);
  const inFlightMapsRef = useRef<Set<Map<string, ChunkInfo | null>>>(new Set());

  const fetchChunks = useCallback(async (id: string) => {
    const seq = ++seqRef.current;
    const ownMap = new Map<string, ChunkInfo | null>();
    inFlightMapsRef.current.add(ownMap);
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("job_chunks")
        .select("*")
        .eq("parent_job_id", id)
        .order("chunk_index", { ascending: true });
      // Réponse pour une tâche qui n'est plus affichée : ignorée.
      if (jobIdRef.current !== id) return;
      if (seq >= lastAppliedSeqRef.current && !error && data) {
        setChunks(mergeChunksSnapshot(data.map(mapChunk), ownMap));
        lastAppliedSeqRef.current = seq;
      }
    } catch {
      // Requête réseau rejetée (coupure, annulation) : rien n'est appliqué —
      // le `finally` reste seul responsable de libérer `isLoading`, sinon le
      // Set ne se vide jamais et l'écran reste bloqué sur le squelette.
    } finally {
      inFlightMapsRef.current.delete(ownMap);
      if (inFlightMapsRef.current.size === 0) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!jobId) {
      setChunks([]);
      return;
    }
    void fetchChunks(jobId);
  }, [jobId, fetchChunks]);

  // Temps réel : lignes `job_chunks` de user:<uid> (spec §5 ligne 36).
  useBroadcast({
    topics: jobId && userId ? [userTopic(userId)] : NO_TOPICS,
    isPrivate: true,
    ownerId: userId,
    events: CHUNK_EVENTS,
    onMessage: (_topic, _event, payload) => {
      const current = jobIdRef.current;
      if (!current) return;
      const effect = computeChunkEffect(payload, current);
      if (effect) {
        for (const map of inFlightMapsRef.current) map.set(effect.id, effect.value);
      }
      setChunks((prev) => applyEffect(prev, effect));
    },
    onResync: () => {
      const current = jobIdRef.current;
      if (current) void fetchChunks(current);
    },
  });

  return { chunks, isLoading };
}

function mapChunk(dbChunk: any): ChunkInfo {
  return {
    id: dbChunk.id,
    chunkIndex: dbChunk.chunk_index,
    totalChunks: dbChunk.total_chunks,
    status: dbChunk.status,
    ordersCount: dbChunk.orders_count,
    storagePath: dbChunk.storage_path,
    errorMessage: dbChunk.error_message,
    startedAt: dbChunk.started_at,
    completedAt: dbChunk.completed_at,
  };
}
